import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { Navbar } from 'src/shared';
import { isAdmin } from 'src/redux/auth/service';
import {
    BackButton,
    EmptyState,
    Layout,
    PageHeader,
    PageTitle,
    RootBox,
    SessionListPanel,
    SkeletonRow,
    TranscriptPanel
} from '../styled';
import SessionList from './SessionList';
import SessionTranscript from './SessionTranscript';
import { LlmSessionInfo } from 'src/types/llmInfo';

interface Props {
    title: string;
    backTo: string;
    fetchSessions: () => Promise<unknown>;
}

const LlmInfoView: FC<Props> = ({ title, backTo, fetchSessions }) => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState<LlmSessionInfo[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

    useEffect(() => {
        if (!isAdmin()) {
            navigate('/');
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        fetchSessions()
            .then((res) => {
                if (cancelled) return;
                const data = (res as { data?: LlmSessionInfo[] })?.data ?? [];
                setSessions(Array.isArray(data) ? data : []);
                if (Array.isArray(data) && data.length > 0) {
                    setActiveSessionId(data[0].sessionId ?? 'unattributed-0');
                }
            })
            .catch(() => {
                if (!cancelled) setSessions([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [fetchSessions, navigate]);

    const activeSession = useMemo<LlmSessionInfo | null>(() => {
        if (!activeSessionId) return null;
        return (
            sessions.find(
                (s, i) =>
                    (s.sessionId ?? `unattributed-${i}`) === activeSessionId
            ) ?? null
        );
    }, [activeSessionId, sessions]);

    return (
        <Navbar>
            <RootBox>
                <PageHeader>
                    <PageTitle>{title}</PageTitle>
                    <BackButton onClick={() => navigate(backTo)}>
                        ← Back
                    </BackButton>
                </PageHeader>

                {isLoading ? (
                    <Layout>
                        <SessionListPanel>
                            <SkeletonRow variant="rectangular" height={60} />
                            <SkeletonRow variant="rectangular" height={60} />
                            <SkeletonRow variant="rectangular" height={60} />
                        </SessionListPanel>
                        <TranscriptPanel>
                            <SkeletonRow variant="rectangular" height={400} />
                        </TranscriptPanel>
                    </Layout>
                ) : sessions.length === 0 ? (
                    <Box>
                        <EmptyState>
                            No LLM sessions recorded yet for this resource.
                        </EmptyState>
                    </Box>
                ) : (
                    <Layout>
                        <SessionList
                            sessions={sessions}
                            activeSessionId={activeSessionId}
                            onSelect={setActiveSessionId}
                        />
                        <SessionTranscript session={activeSession} />
                    </Layout>
                )}
            </RootBox>
        </Navbar>
    );
};

export default LlmInfoView;
